import { Box, Button, Heading, Spinner, Text, VStack } from '@chakra-ui/react';
import { APPOINTMENT_ROUTE } from '@web-app/components/consts';
import { useOrganizationLogo } from '@web-app/hooks/whitelabel/useOrganizationLogo';
import Image from 'next/image';
import Link from 'next/link';

import { ERROR_CONFIGS, styles } from './config';
import { RoomErrorScreenProps, RoomErrorState } from './types';

/**
 * Gets the dynamic message from state if available
 */
const getDynamicMessage = (state: RoomErrorState): string | undefined => {
  if ('message' in state) {
    return state.message;
  }
  return undefined;
};

/**
 * Error screen component for Daily room issues.
 * Displays different content based on error type with consistent styling.
 */
export const RoomErrorScreen = ({
  state,
  onReload,
}: RoomErrorScreenProps): JSX.Element => {
  const logoConfig = useOrganizationLogo();
  const config = ERROR_CONFIGS[state.type];
  const dynamicMessage = getDynamicMessage(state);

  return (
    <VStack spacing={0} sx={styles.container}>
      {/* Logo header */}
      <Box sx={styles.logoContainer}>
        {logoConfig &&
          (config.logoClickable !== false ? (
            <Link href={APPOINTMENT_ROUTE}>
              <Image
                src={logoConfig.getLogoSrc({ logoColor: 'dark' })}
                alt={logoConfig.altText}
                width={200}
                height={50}
                style={{ objectFit: 'contain', cursor: 'pointer' }}
              />
            </Link>
          ) : (
            <Image
              src={logoConfig.getLogoSrc({ logoColor: 'dark' })}
              alt={logoConfig.altText}
              width={200}
              height={50}
              style={{ objectFit: 'contain' }}
            />
          ))}
      </Box>

      {/* Centered content */}
      <VStack sx={styles.contentWrapper}>
        <Box sx={styles.card}>
          <VStack spacing={2}>
            <Heading as="h1" size="lg" sx={styles.heading}>
              {config.title}
            </Heading>

            {config.showSpinner && (
              <Spinner size="xl" color="green.400" my={6} />
            )}

            <Text sx={styles.primaryText}>{config.description}</Text>

            {/* Dynamic message from error state */}
            {dynamicMessage && (
              <Text sx={styles.secondaryText} mb={config.showButton ? 6 : 0}>
                {dynamicMessage}
              </Text>
            )}

            {/* Static secondary text from config */}
            {config.secondaryText && !dynamicMessage && (
              <Text sx={styles.secondaryText}>{config.secondaryText}</Text>
            )}

            {config.showButton &&
              (config.buttonHref ? (
                <Button
                  as="a"
                  href={config.buttonHref}
                  colorScheme={config.buttonColorScheme}
                  _hover={{ bg: config.buttonHoverBg, textDecoration: 'none' }}
                  size="md"
                  mt={4}
                >
                  {config.buttonText}
                </Button>
              ) : (
                <Button
                  colorScheme={config.buttonColorScheme}
                  _hover={{ bg: config.buttonHoverBg }}
                  size="md"
                  mt={4}
                  onClick={onReload}
                >
                  {config.buttonText}
                </Button>
              ))}
          </VStack>
        </Box>
      </VStack>
    </VStack>
  );
};
